import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { AutoField, FieldLabel } from "@puckeditor/core";
import lottie, { type AnimationItem } from "lottie-web";
import {
  CONTENT_WIDTHS,
  PAGE_TYPES,
  buildEditorComponentMap,
  fontSize,
  getLottiePresetById,
  getComponentsForPageType,
  listLottiePresets,
  spacing,
  type PageType,
  SHARED_BLOCK_REGISTRY,
} from "@ease/page-builder";
import { Testimonials, PricingTable, FaqAccordion, VideoEmbed, Gallery, FeatureGrid, StatsCounter, ContactForm } from "./puck-components";
import {
  Container, Grid, Tabs, StickySection, Carousel, Timeline, Blockquote, CodeBlock,
  Alert, DataTable, VideoBackground, BeforeAfter, ImageHotspot, ClientLogos, CaseStudy,
  Awards, SocialFeed, Counter, RoiCalculator, SocialProofPopup, Survey, AppointmentForm, Newsletter, FileUpload, GiftCard,
  Coupon, PaymentButton, WaitlistForm, CallBooking, MembershipPlans, LanguageSwitcher,
  ClassSchedule, StaffSelector,
} from "./puck-components-wave3";
import {
  BlogListingGrid, BlogPostCard, BookingWidget, CountdownTimer, CtaBanner, HeroSection, LeadForm, LogoCloud, MultiStepForm,
  NavigationBar, SocialProof, ComparisonTable, EmbedWidget, VideoPlayer,
  ProgressBar, DripGate, TextBlock, QuizBlock, ExerciseBlock, PopupTrigger, TrackingPixel, OrderBump,
  // PB-N: canonical shared-block editor mounts
  ButtonGroup, ServicesList, ContactInfo, OpeningHours, TeamGrid,
} from "./puck-components-renderers";
import {
  Section as SectionBlock, RichText, FeatureCard, IconText, Spacer, Divider,
  Accordion, Footer as FooterBlock, NotificationBar, BackToTop, SocialIcons,
  ImageEnhanced, OfferCard, ServiceMenu, ChatWidget, FloatingCta,
  ScrollTriggeredCta, Breadcrumb, AiRecommendation, MapEmbed,
} from "./puck-components-wave2";
import { createImageField } from "@/components/pages/puck-authoring-fields";
import {
  buildFlexGridStyles,
  createFlexGridField,
  normalizeFlexGridFieldValue,
} from "@/components/pages/fields/flex-grid-controls";
import { sanitizeHref, sanitizeImageSrc, safeColumns } from "./editor-utils";
import { withStylablePreview } from "./editor-preview";
import { wrapAllComponents } from "@/lib/puck-dss-wrapper";
import { apiFetch } from "@/lib/api-client";

const PUCK_COMPONENT_CATEGORIES = {
  Layout: [
    "Container",
    "SectionBlock",
    "Columns",
    "Grid",
    "Spacer",
    "Divider",
    "StickySection",
  ],
  Hero: [
    "hero-section",
    "Hero",
    "WebinarRegistration",
    "VideoBackground",
  ],
  Content: [
    "Heading",
    "Text",
    "RichText",
    "Image",
    "TextBlock",
    "blog-post-card",
    "blog-listing-grid",
    "FeatureCard",
    "Blockquote",
    "CodeBlock",
    "IconText",
    "Alert",
  ],
  "Social Proof": [
    "Testimonials",
    "Reviews",
    "StatsCounter",
    "LogoCloud",
    "ClientLogos",
    "SocialProof",
    "SocialProofPopup",
    "Awards",
    "CaseStudy",
    "SocialFeed",
    "Counter",
  ],
  Conversion: [
    "Button",
    "ButtonGroup",
    "ContactForm",
    "LeadForm",
    "MultiStepForm",
    "BookingWidget",
    "CtaBanner",
    "Newsletter",
    "Survey",
    "AppointmentForm",
    "WaitlistForm",
    "CallBooking",
    "popup-trigger",
    "FloatingCta",
    "ScrollTriggeredCta",
    "ChatWidget",
  ],
  Navigation: [
    "NavigationBar",
    "FooterBlock",
    "Breadcrumb",
    "BackToTop",
    "SocialIcons",
    "LanguageSwitcher",
    "NotificationBar",
  ],
  "Data Display": [
    "FaqAccordion",
    "FeatureGrid",
    "calendar-embed",
    "Timeline",
    "Tabs",
    "Accordion",
    "DataTable",
    "ComparisonTable",
    "ProgressBar",
    "ClassSchedule",
    "StaffSelector",
    "ServicesList",
    "ContactInfo",
    "OpeningHours",
    "TeamGrid",
  ],
  Commerce: [
    "PricingTable",
    "RoiCalculator",
    "OfferCard",
    "ServiceMenu",
    "Coupon",
    "GiftCard",
    "order-bump",
    "PaymentButton",
    "MembershipPlans",
  ],
  Media: [
    "VideoEmbed",
    "Gallery",
    "BeforeAfter",
    "Carousel",
    "ImageEnhanced",
    "ImageHotspot",
    "EmbedWidget",
    "VideoPlayer",
    "LottiePlayer",
    "MapEmbed",
    "CountdownTimer",
  ],
  Advanced: [
    "QuizBlock",
    "ExerciseBlock",
    "AiRecommendation",
    "tracking-pixel",
    "FileUpload",
    "DripGate",
  ],
} as const;

type LottieTrigger = "none" | "hover" | "scroll-into-view" | "click";

interface LottieSourceFieldConfig {
  label: string;
}

type LottiePresetVertical = "all" | "dental" | "yoga" | "restaurant" | "generic";
const LOTTIE_PRESET_PREVIEW_LIMIT = 24;

interface PuckCustomFieldProps<TField> {
  field: TField;
  onChange: (value: unknown, uiState?: Record<string, unknown>) => void;
  value: unknown;
  readOnly?: boolean;
}

function buildAssetAbsoluteUrl(assetId: string): string {
  const path = `/crm/pages/assets/${assetId}/file`;
  if (typeof window === "undefined") {
    return path;
  }
  return `${window.location.origin}${path}`;
}

function createLottieSourceField(config: LottieSourceFieldConfig) {
  return {
    type: "custom" as const,
    ...config,
    render: LottieSourceFieldEditor,
  };
}

function LottieSourceFieldEditor({
  field,
  onChange,
  readOnly,
  value,
}: PuckCustomFieldProps<LottieSourceFieldConfig>) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [presetFilter, setPresetFilter] = useState<LottiePresetVertical>("all");
  const [presetSearch, setPresetSearch] = useState("");

  const currentValue = typeof value === "string" ? value : "";
  const filteredPresets = useMemo(() => {
    const normalizedQuery = presetSearch.trim().toLowerCase();
    return listLottiePresets(presetFilter).filter((preset) => {
      if (!normalizedQuery) {
        return true;
      }

      const searchableText = [
        preset.name,
        preset.description,
        preset.category,
        preset.vertical,
      ].join(" ").toLowerCase();

      return searchableText.includes(normalizedQuery);
    });
  }, [presetFilter, presetSearch]);
  const visiblePresets = useMemo(
    () => filteredPresets.slice(0, LOTTIE_PRESET_PREVIEW_LIMIT),
    [filteredPresets],
  );
  const presetPreviewUrls = useMemo(() => {
    return visiblePresets.map((preset) => ({
      preset,
      url: URL.createObjectURL(new Blob([JSON.stringify(preset.animationData)], { type: "application/json" })),
    }));
  }, [visiblePresets]);

  useEffect(() => {
    return () => {
      for (const entry of presetPreviewUrls) {
        URL.revokeObjectURL(entry.url);
      }
    };
  }, [presetPreviewUrls]);

  const uploadLottiePayload = async (filename: string, payload: Blob) => {
    const formData = new FormData();
    formData.append("file", new File([payload], filename, { type: "application/json" }));

    const result = await apiFetch<{ data: { id: string } }>("/crm/pages/assets", {
      method: "POST",
      body: formData,
    });

    return buildAssetAbsoluteUrl(result.data.id);
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (file.type !== "application/json" || !file.name.toLowerCase().endsWith(".json")) {
      setError("Upload a `.json` Lottie file.");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError("Lottie files must be 2 MB or smaller.");
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const uploadedUrl = await uploadLottiePayload(file.name, file);
      onChange(uploadedUrl);
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : "Lottie upload failed.");
    } finally {
      setIsUploading(false);
    }
  };

  const handlePresetInstall = async (presetId: string) => {
    const preset = getLottiePresetById(presetId);
    if (!preset) return;

    setIsUploading(true);
    setError(null);

    try {
      const nextSource = await uploadLottiePayload(
        `${preset.id}.json`,
        new Blob([JSON.stringify(preset.animationData)], { type: "application/json" }),
      );
      onChange(nextSource);
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : "Preset install failed.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <FieldLabel label={field.label}>
      <div className="space-y-3 rounded-lg border border-border bg-background/60 p-3">
        <div className="space-y-2 rounded-md border border-border bg-card/70 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-medium text-foreground">Preset Library</p>
              <p className="text-xs text-muted-foreground">Curated vertical motion accents installed into tenant assets on selection.</p>
            </div>
            <div className="flex flex-wrap gap-1">
              {(["all", "dental", "yoga", "restaurant", "generic"] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setPresetFilter(option)}
                  className={`rounded-full px-2.5 py-1 text-[11px] transition-colors ${
                    presetFilter === option
                      ? "bg-primary/10 text-primary"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  {option === "all" ? "All" : option}
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
            <input
              type="search"
              value={presetSearch}
              onChange={(event) => setPresetSearch(event.target.value)}
              placeholder="Search presets by name, vertical, or use case"
              disabled={readOnly || isUploading}
              className="min-w-0 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground shadow-sm outline-none transition focus:border-primary/40 focus:ring-2 focus:ring-primary/20"
            />
            <div className="flex items-center rounded-md border border-border bg-background px-3 text-xs text-muted-foreground">
              Showing {visiblePresets.length} of {filteredPresets.length}
            </div>
          </div>
          {filteredPresets.length > LOTTIE_PRESET_PREVIEW_LIMIT ? (
            <p className="text-xs text-muted-foreground">
              Refine by vertical or search to narrow the library before previewing more presets.
            </p>
          ) : null}
          <div className="grid gap-2 sm:grid-cols-2">
            {presetPreviewUrls.map(({ preset, url }) => (
              <button
                key={preset.id}
                type="button"
                disabled={readOnly || isUploading}
                onClick={() => void handlePresetInstall(preset.id)}
                className="rounded-lg border border-border bg-background p-3 text-left transition hover:border-primary/40 hover:bg-primary/5 disabled:opacity-60"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">{preset.name}</p>
                    <p className="mt-1 text-[11px] uppercase tracking-[0.08em] text-muted-foreground">{preset.vertical}</p>
                  </div>
                  <div className="rounded-full bg-muted px-2 py-1 text-[10px] text-muted-foreground">
                    {preset.category}
                  </div>
                </div>
                <div className="mt-3 rounded-md border border-border bg-muted/20 p-2">
                  <LottiePreview
                    autoplay="yes"
                    height="96px"
                    loop="yes"
                    source={url}
                    speed={1}
                    trigger="none"
                    width="100%"
                  />
                </div>
                <p className="mt-2 text-xs text-muted-foreground">{preset.description}</p>
              </button>
            ))}
          </div>
          {!presetPreviewUrls.length ? (
            <div className="rounded-md border border-dashed border-border bg-muted/20 px-4 py-6 text-center text-sm text-muted-foreground">
              No presets match the current filters.
            </div>
          ) : null}
        </div>
        <AutoField
          field={{
            type: "text",
            label: "Lottie JSON URL",
            placeholder: "https://cdn.example.com/animation.json",
          }}
          onChange={(nextValue) => {
            setError(null);
            onChange(nextValue);
          }}
          readOnly={readOnly}
          value={currentValue}
        />
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex cursor-pointer items-center rounded-md border border-border px-3 py-2 text-sm text-foreground disabled:opacity-50">
            <input
              type="file"
              accept="application/json,.json"
              className="hidden"
              disabled={readOnly || isUploading}
              onChange={handleFileChange}
            />
            {isUploading ? "Uploading..." : "Upload JSON to tenant assets"}
          </label>
          <span className="text-xs text-muted-foreground">
            Stored via `/crm/pages/assets`, validated server-side, then reused by published pages.
          </span>
        </div>
        {error ? (
          <p className="text-xs text-destructive">{error}</p>
        ) : null}
      </div>
    </FieldLabel>
  );
}

function LottiePreview({
  source,
  autoplay,
  loop,
  speed,
  trigger,
  width,
  height,
}: {
  source: string;
  autoplay: "yes" | "no";
  loop: "yes" | "no";
  speed: number;
  trigger: LottieTrigger;
  width: string;
  height: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const animationRef = useRef<AnimationItem | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !source) {
      if (animationRef.current) {
        animationRef.current.destroy();
        animationRef.current = null;
      }
      return;
    }

    let isActive = true;
    let observer: IntersectionObserver | null = null;
    let onMouseEnter: (() => void) | null = null;
    let onMouseLeave: (() => void) | null = null;
    let onClick: (() => void) | null = null;

    setError(null);
    container.innerHTML = "";

    void (async () => {
      try {
        const response = await fetch(source);
        if (!response.ok) {
          throw new Error(`Failed to load animation (${response.status})`);
        }

        const animationData = await response.json();
        if (!isActive || !containerRef.current) {
          return;
        }

        const shouldAutoplay = trigger === "none" && autoplay === "yes";
        const animation = lottie.loadAnimation({
          container: containerRef.current,
          renderer: "svg",
          loop: loop === "yes",
          autoplay: shouldAutoplay,
          animationData,
        });
        animation.setSpeed(Math.max(0.1, Math.min(5, speed || 1)));
        animationRef.current = animation;

        if (trigger === "hover") {
          animation.stop();
          onMouseEnter = () => animation.goToAndPlay(0, true);
          onMouseLeave = () => animation.stop();
          container.addEventListener("mouseenter", onMouseEnter);
          container.addEventListener("mouseleave", onMouseLeave);
        }

        if (trigger === "click") {
          animation.stop();
          onClick = () => {
            if (animation.isPaused) {
              animation.play();
              return;
            }
            animation.pause();
          };
          container.addEventListener("click", onClick);
        }

        if (trigger === "scroll-into-view") {
          animation.stop();
          observer = new IntersectionObserver(([entry]) => {
            if (!entry) return;
            if (entry.isIntersecting) {
              animation.play();
              return;
            }
            animation.stop();
          }, { threshold: 0.35 });
          observer.observe(container);
        }
      } catch (loadError) {
        if (!isActive) return;
        setError(loadError instanceof Error ? loadError.message : "Failed to load Lottie preview.");
      }
    })();

    return () => {
      isActive = false;
      observer?.disconnect();
      if (container && onMouseEnter) container.removeEventListener("mouseenter", onMouseEnter);
      if (container && onMouseLeave) container.removeEventListener("mouseleave", onMouseLeave);
      if (container && onClick) container.removeEventListener("click", onClick);
      animationRef.current?.destroy();
      animationRef.current = null;
    };
  }, [autoplay, loop, source, speed, trigger]);

  const previewHeight = height === "auto" ? "240px" : height;

  return (
    <div className="rounded-xl border border-border bg-background/80 p-4">
      {!source ? (
        <div className="flex min-h-40 items-center justify-center rounded-lg border border-dashed border-border bg-muted/30 px-4 text-center text-sm text-muted-foreground">
          Paste a Lottie JSON URL or upload one to preview it in the canvas.
        </div>
      ) : (
        <div
          ref={containerRef}
          className="mx-auto overflow-hidden rounded-lg border border-border bg-muted/20"
          style={{ width, height: previewHeight, minHeight: previewHeight === "auto" ? undefined : previewHeight }}
        />
      )}
      {error ? <p className="mt-3 text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

const LottiePlayer = {
  fields: {
    source: createLottieSourceField({ label: "Source" }),
    autoplay: {
      type: "select",
      label: "Autoplay",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    },
    loop: {
      type: "select",
      label: "Loop",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    },
    speed: { type: "number", label: "Speed" },
    trigger: {
      type: "select",
      label: "Trigger",
      options: [
        { label: "None", value: "none" },
        { label: "Hover", value: "hover" },
        { label: "Scroll Into View", value: "scroll-into-view" },
        { label: "Click", value: "click" },
      ],
    },
    width: { type: "text", label: "Width" },
    height: { type: "text", label: "Height" },
  },
  defaultProps: {
    source: "",
    autoplay: "yes",
    loop: "yes",
    speed: 1,
    trigger: "none",
    width: "100%",
    height: "auto",
  },
  render: ({
    source,
    autoplay,
    loop,
    speed,
    trigger,
    width,
    height,
  }: {
    source: string;
    autoplay: "yes" | "no";
    loop: "yes" | "no";
    speed: number;
    trigger: LottieTrigger;
    width: string;
    height: string;
  }) => (
    <LottiePreview
      autoplay={autoplay}
      height={height}
      loop={loop}
      source={source}
      speed={speed}
      trigger={trigger}
      width={width}
    />
  ),
};

const sharedPuckComponents = buildEditorComponentMap(SHARED_BLOCK_REGISTRY, {
  "hero-section": HeroSection,
  "calendar-embed": {
    ...BookingWidget,
    defaultProps: {
      ...BookingWidget.defaultProps,
      heading: "Book Your Appointment",
    },
  },
  "popup-trigger": PopupTrigger,
  "tracking-pixel": TrackingPixel,
  "blog-post-card": BlogPostCard,
  "blog-listing-grid": BlogListingGrid,
  "order-bump": OrderBump,
  // PB-N: editor mounts for the canonical shared blocks added in #918. The
  // runtime renderers in @ease/page-builder are already used by the
  // published-page serve path; the editor stubs above let authors place
  // these blocks in the canvas.
  ButtonGroup,
  ServicesList,
  ContactInfo,
  OpeningHours,
  TeamGrid,
  BookingWidget,
  CountdownTimer,
  CtaBanner,
  LeadForm,
  LogoCloud,
  MultiStepForm,
  NavigationBar,
  SocialProof,
  ComparisonTable,
  EmbedWidget,
  VideoPlayer,
  ProgressBar,
  DripGate,
  QuizBlock,
  ExerciseBlock,
  TextBlock,
  Testimonials,
  PricingTable,
  FaqAccordion,
  VideoEmbed,
  Gallery,
  FeatureGrid,
  StatsCounter,
  ChatWidget,
  LottiePlayer,
  // RV-B: live moderated-reviews block. Editor renders a placeholder card
  // because real reviews are hydrated by the published-reviews loader at
  // render time. See packages/page-builder/src/renderer/components/reviews-grid.ts.
  Reviews: {
    fields: {
      heading: { type: "text" as const, label: "Heading" },
      columns: { type: "number" as const, label: "Columns (1-4)", min: 1, max: 4 },
      showRating: {
        type: "select" as const,
        label: "Show star rating",
        options: [
          { label: "Yes", value: "yes" },
          { label: "No", value: "no" },
        ],
      },
      showSource: {
        type: "select" as const,
        label: "Show source platform",
        options: [
          { label: "Yes", value: "yes" },
          { label: "No", value: "no" },
        ],
      },
      emptyStateMessage: { type: "text" as const, label: "Empty state message" },
      minRating: { type: "number" as const, label: "Min rating to show", min: 1, max: 5 },
    },
    defaultProps: {
      heading: "What Customers Are Saying",
      columns: 3,
      showRating: "yes",
      showSource: "yes",
      emptyStateMessage: "Be the first to share your experience.",
      minRating: 4,
    },
    render: ({ heading }: { heading?: string }) => {
      // Editor preview only — published render uses the live reviews-grid
      // renderer hydrated from approved review-widget decisions.
      return (
        <section className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-muted)]/30 p-8 text-center">
          <h2 className="text-2xl font-semibold">{heading ?? "What Customers Are Saying"}</h2>
          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
            Live moderated reviews appear here on published pages.
          </p>
        </section>
      );
    },
  },
});

function buildPreviewBoxes(count: number) {
  return Array.from({ length: count }, (_, index) => (
    <div
      key={index}
      style={{
        minHeight: "64px",
        borderRadius: "8px",
        border: "1px dashed var(--color-border)",
        background: "var(--color-muted)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--color-muted-foreground)",
        fontSize: "0.75rem",
      }}
    >
      Item {index + 1}
    </div>
  ));
}

function countGridColumns(customColumns: string, fallback: number) {
  const normalized = customColumns.trim();
  if (!normalized) {
    return fallback;
  }

  return normalized.split(/\s+/).length;
}

const layoutField = createFlexGridField({ label: "Layout" });

const containerWithVisualLayout = {
  ...Container,
  fields: {
    ...Container.fields,
    layout: layoutField,
  },
  defaultProps: {
    ...Container.defaultProps,
    layout: undefined,
  },
  render: ({
    maxWidth,
    padding,
    background,
    layout,
  }: {
    maxWidth: string;
    padding: string;
    background: string;
    layout?: unknown;
  }) => {
    const layoutConfig = normalizeFlexGridFieldValue(layout);
    const previewCount =
      layoutConfig.display === "grid"
        ? Math.max(countGridColumns(layoutConfig.grid.customColumns, layoutConfig.grid.columns), 2)
        : 3;

    return (
      <div
        style={{
          maxWidth: maxWidth === "100%" ? "100%" : `${maxWidth}px`,
          margin: "0 auto",
          padding: `${padding}px`,
          background,
          borderRadius: "12px",
        }}
      >
        <div
          style={{
            minHeight: "120px",
            border: "1px dashed var(--color-border)",
            borderRadius: "8px",
            padding: "16px",
            ...buildFlexGridStyles(layout),
          }}
        >
          {buildPreviewBoxes(previewCount)}
        </div>
      </div>
    );
  },
};

const sectionBlockWithVisualLayout = {
  ...SectionBlock,
  fields: {
    ...SectionBlock.fields,
    layout: layoutField,
  },
  defaultProps: {
    ...SectionBlock.defaultProps,
    layout: undefined,
  },
  render: ({
    backgroundColor,
    backgroundImage,
    padding,
    maxWidth,
    overlay,
    layout,
  }: {
    backgroundColor: string;
    backgroundImage: string;
    padding: string;
    maxWidth: string;
    overlay: string;
    layout?: unknown;
  }) => {
    const layoutConfig = normalizeFlexGridFieldValue(layout);
    const overlayBg =
      overlay === "dark"
        ? "rgba(0,0,0,0.5)"
        : overlay === "light"
          ? "rgba(255,255,255,0.5)"
          : "transparent";
    const padMap: Record<string, string> = { sm: spacing(3), md: spacing(6), lg: spacing(9), xl: spacing(12) };
    const previewCount =
      layoutConfig.display === "grid"
        ? Math.max(countGridColumns(layoutConfig.grid.customColumns, layoutConfig.grid.columns), 2)
        : 3;

    return (
      <div
        style={{
          padding: padMap[padding] ?? spacing(9),
          background: backgroundImage
            ? `url(${backgroundImage}) center/cover no-repeat`
            : backgroundColor || "var(--color-surface)",
          position: "relative",
          borderRadius: "12px",
        }}
      >
        {overlay !== "none" && (
          <div style={{ position: "absolute", inset: 0, background: overlayBg, borderRadius: "12px" }} />
        )}
        <div style={{ maxWidth, margin: "0 auto", position: "relative" }}>
          <div
            style={{
              minHeight: "120px",
              border: "1px dashed var(--color-border)",
              borderRadius: "8px",
              padding: "16px",
              background: "color-mix(in srgb, var(--color-background) 82%, transparent)",
              ...buildFlexGridStyles(layout),
            }}
          >
            {buildPreviewBoxes(previewCount)}
          </div>
        </div>
      </div>
    );
  },
};

const columnsWithVisualLayout = {
  fields: {
    columns: { type: "number" },
    layout: layoutField,
  },
  defaultProps: {
    columns: 2,
    layout: {
      display: "grid",
      grid: {
        columns: 2,
      },
    },
  },
  render: ({ columns, layout }: { columns: number; layout?: unknown }) => {
    const count = safeColumns(columns);
    const layoutConfig = normalizeFlexGridFieldValue(layout);
    const layoutStyles =
      layoutConfig.display === "block"
        ? {
            display: "grid" as const,
            gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`,
            gap: "20px",
          }
        : buildFlexGridStyles(
            layoutConfig.display === "grid"
              ? {
                  ...layoutConfig,
                  grid: {
                    ...layoutConfig.grid,
                    columns: layoutConfig.grid.customColumns.trim()
                      ? layoutConfig.grid.columns
                      : count,
                  },
                }
              : layoutConfig,
          );

    return (
      <div style={layoutStyles}>
        {Array.from({ length: count }, (_, index) => (
          <div
            key={index}
            style={{
              padding: "20px",
              background: "var(--color-muted)",
              borderRadius: "8px",
              minHeight: "100px",
              border: "1px dashed var(--color-border)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--color-muted-foreground)",
            }}
          >
            Column {index + 1}
          </div>
        ))}
      </div>
    );
  },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Puck config typing is complex, validated at runtime
export const puckConfig: any = {
  categories: PUCK_COMPONENT_CATEGORIES,
  components: {
    Heading: {
      fields: {
        text: { type: "text" },
        level: {
          type: "select",
          options: [
            { label: "H1", value: "h1" },
            { label: "H2", value: "h2" },
            { label: "H3", value: "h3" },
          ],
        },
        align: {
          type: "select",
          options: [
            { label: "Left", value: "left" },
            { label: "Center", value: "center" },
            { label: "Right", value: "right" },
          ],
        },
      },
      defaultProps: { text: "Your heading here", level: "h2", align: "left" },
      render: ({ text, level, align }: { text: string; level: string; align: string }) => {
        const style = { textAlign: align as "left" | "center" | "right" };
        if (level === "h1") return <h1 style={style}>{text}</h1>;
        if (level === "h3") return <h3 style={style}>{text}</h3>;
        return <h2 style={style}>{text}</h2>;
      },
    },
    Text: {
      fields: {
        content: { type: "textarea" },
        align: {
          type: "select",
          options: [
            { label: "Left", value: "left" },
            { label: "Center", value: "center" },
          ],
        },
      },
      defaultProps: { content: "Your text content here.", align: "left" },
      render: ({ content, align }: { content: string; align: string }) => (
        <p style={{ textAlign: align as "left" | "center" }}>{content}</p>
      ),
    },
    Image: {
      fields: {
        src: createImageField({ label: "Image", helperText: "Choose from your media library or upload to R2." }),
        alt: { type: "text" },
      },
      defaultProps: { src: "", alt: "Image description" },
      render: ({ src, alt }: { src: string; alt: string }) => (
        <div style={{ textAlign: "center" }}>
          {sanitizeImageSrc(src) ? (
            <img src={sanitizeImageSrc(src)} alt={alt} style={{ maxWidth: "100%", borderRadius: "8px" }} />
          ) : (
            <div style={{ background: "var(--color-muted)", padding: "40px", borderRadius: "8px", color: "var(--color-muted-foreground)" }}>
              Image placeholder — set URL in sidebar
            </div>
          )}
        </div>
      ),
    },
    Button: {
      fields: {
        label: { type: "text" },
        url: { type: "text" },
        variant: {
          type: "select",
          options: [
            { label: "Primary", value: "primary" },
            { label: "Secondary", value: "secondary" },
            { label: "Outline", value: "outline" },
          ],
        },
      },
      defaultProps: { label: "Click me", url: "#", variant: "primary" },
      render: ({ label, url, variant }: { label: string; url: string; variant: string }) => {
        const styles: Record<string, React.CSSProperties> = {
          primary: { background: "var(--color-primary)", color: "white", padding: "10px 24px", borderRadius: "6px", border: "none", cursor: "pointer", fontWeight: 500 },
          secondary: { background: "var(--color-muted-foreground)", color: "white", padding: "10px 24px", borderRadius: "6px", border: "none", cursor: "pointer", fontWeight: 500 },
          outline: { background: "transparent", color: "var(--color-primary)", padding: "10px 24px", borderRadius: "6px", border: "1px solid var(--color-primary)", cursor: "pointer", fontWeight: 500 },
        };
        return (
          <div style={{ textAlign: "center" }}>
            <a href={sanitizeHref(url)} style={{ ...styles[variant], display: "inline-block", textDecoration: "none" }}>
              {label}
            </a>
          </div>
        );
      },
    },
    Hero: {
      fields: {
        title: { type: "text" },
        subtitle: { type: "textarea" },
        ctaLabel: { type: "text" },
        ctaUrl: { type: "text" },
      },
      defaultProps: {
        title: "Welcome to our site",
        subtitle: "The best platform for your business",
        ctaLabel: "Get Started",
        ctaUrl: "/book",
        _dssStyles: {
          background: {
            mode: "gradient",
            opacity: 100,
            color: "",
            gradient: {
              type: "linear",
              angle: 135,
              radialShape: "ellipse",
              radialCenterX: 50,
              radialCenterY: 50,
              stops: [
                { color: "var(--color-primary)", position: 0 },
                { color: "var(--color-ai)", position: 100 },
              ],
            },
            image: {
              url: "",
              positionX: 50,
              positionY: 50,
              size: "cover",
              repeat: "no-repeat",
              attachment: "scroll",
              customWidth: "",
              customHeight: "",
            },
            video: {
              url: "",
              autoplay: false,
              loop: false,
              muted: true,
            },
          },
        },
      },
      render: ({ title, subtitle, ctaLabel, ctaUrl }: { title: string; subtitle: string; ctaLabel: string; ctaUrl: string }) => (
        <div style={{ textAlign: "center", padding: `${spacing(15)} ${spacing(3)}`, color: "white", borderRadius: "12px" }}>
          <h1 style={{ fontSize: fontSize("3xl"), fontWeight: "bold", marginBottom: spacing(2) }}>{title}</h1>
          <p style={{ fontSize: fontSize("md"), opacity: 0.9, maxWidth: CONTENT_WIDTHS.sm, margin: `0 auto ${spacing(3)}` }}>{subtitle}</p>
          <a href={sanitizeHref(ctaUrl)} style={{ display: "inline-block", background: "white", color: "var(--color-primary)", padding: `${spacing(2)} ${spacing(4)}`, borderRadius: "8px", fontWeight: 600, textDecoration: "none" }}>
            {ctaLabel}
          </a>
        </div>
      ),
    },
    Columns: columnsWithVisualLayout,
    WebinarRegistration: {
      fields: {
        webinarTitle: { type: "text" },
        webinarDate: { type: "text" },
        webinarTime: { type: "text" },
        description: { type: "textarea" },
        ctaLabel: { type: "text" },
        showCountdown: {
          type: "select",
          options: [
            { label: "Yes", value: "yes" },
            { label: "No", value: "no" },
          ],
        },
        showSocialShare: {
          type: "select",
          options: [
            { label: "Yes", value: "yes" },
            { label: "No", value: "no" },
          ],
        },
        webinarId: { type: "text" },
      },
      defaultProps: {
        webinarTitle: "Join Our Live Webinar",
        webinarDate: "2026-04-15",
        webinarTime: "10:00 AM EST",
        description: "Learn the strategies that top performers use to grow their business.",
        ctaLabel: "Register Now — Free",
        showCountdown: "yes",
        showSocialShare: "yes",
        webinarId: "",
      },
      render: ({ webinarTitle, webinarDate, webinarTime, description, ctaLabel, showCountdown, showSocialShare }: {
        webinarTitle: string;
        webinarDate: string;
        webinarTime: string;
        description: string;
        ctaLabel: string;
        showCountdown: string;
        showSocialShare: string;
      }) => (
        <div style={{ maxWidth: CONTENT_WIDTHS.sm, margin: "0 auto", padding: `${spacing(5)} ${spacing(3)}`, textAlign: "center" }}>
          <h2 style={{ fontSize: fontSize("2xl"), fontWeight: "bold", marginBottom: spacing(1) }}>{webinarTitle}</h2>
          <p style={{ color: "var(--color-muted-foreground)", marginBottom: "16px" }}>
            {webinarDate} at {webinarTime}
          </p>
          {showCountdown === "yes" && (
            <div style={{ display: "flex", justifyContent: "center", gap: spacing(2), marginBottom: spacing(3) }}>
              {["Days", "Hours", "Min", "Sec"].map((unit) => (
                <div key={unit} style={{ textAlign: "center" }}>
                  <div style={{ fontSize: fontSize("xl"), fontWeight: "bold", background: "var(--color-muted)", borderRadius: "8px", padding: `${spacing(1)} ${spacing(2)}` }}>00</div>
                  <span style={{ fontSize: fontSize("xs"), color: "var(--color-muted-foreground)" }}>{unit}</span>
                </div>
              ))}
            </div>
          )}
          <p style={{ color: "var(--color-muted-foreground)", marginBottom: spacing(3), maxWidth: CONTENT_WIDTHS.xs, margin: `0 auto ${spacing(3)}` }}>
            {description}
          </p>
          <div style={{ maxWidth: "400px", margin: "0 auto" }}>
            <input
              type="text"
              placeholder="Your name"
              style={{ width: "100%", padding: `${spacing(1)} ${spacing(2)}`, border: "1px solid var(--color-border)", borderRadius: "6px", marginBottom: spacing(1), boxSizing: "border-box" }}
            />
            <input
              type="email"
              placeholder="Your email"
              style={{ width: "100%", padding: `${spacing(1)} ${spacing(2)}`, border: "1px solid var(--color-border)", borderRadius: "6px", marginBottom: spacing(2), boxSizing: "border-box" }}
            />
            <button style={{ width: "100%", padding: spacing(2), background: "var(--color-primary)", color: "white", border: "none", borderRadius: "6px", fontWeight: 600, fontSize: fontSize("base"), cursor: "pointer" }}>
              {ctaLabel}
            </button>
          </div>
          {showSocialShare === "yes" && (
            <div style={{ marginTop: spacing(3), display: "flex", justifyContent: "center", gap: spacing(2) }}>
              <span style={{ fontSize: fontSize("sm"), color: "var(--color-muted-foreground)" }}>Share:</span>
              {["Twitter", "LinkedIn", "Facebook"].map((platform) => (
                <a key={platform} href="#" style={{ fontSize: fontSize("sm"), color: "var(--color-primary)", textDecoration: "none" }}>
                  {platform}
                </a>
              ))}
            </div>
          )}
        </div>
      ),
    },
    ...sharedPuckComponents,
    ContactForm,
    // Wave 3: local-only components
    Container: containerWithVisualLayout,
    Grid,
    Tabs,
    StickySection,
    Carousel,
    Timeline,
    Blockquote,
    CodeBlock,
    Alert,
    DataTable,
    VideoBackground,
    BeforeAfter,
    ImageHotspot,
    ClientLogos,
    CaseStudy,
    Awards,
    SocialFeed,
    Counter,
    RoiCalculator,
    SocialProofPopup,
    Survey,
    AppointmentForm,
    Newsletter,
    FileUpload,
    GiftCard,
    Coupon,
    PaymentButton,
    WaitlistForm,
    CallBooking,
    MembershipPlans,
    LanguageSwitcher,
    ClassSchedule,
    StaffSelector,
    // Wave 2 local-only components
    SectionBlock: sectionBlockWithVisualLayout,
    RichText,
    FeatureCard,
    IconText,
    Spacer,
    Divider,
    Accordion,
    FooterBlock,
    NotificationBar,
    BackToTop,
    SocialIcons,
    ImageEnhanced,
    OfferCard,
    ServiceMenu,
    FloatingCta,
    ScrollTriggeredCta,
    Breadcrumb,
    AiRecommendation,
    MapEmbed,
    // Local renderer-preview components
    ProgressBar,
    QuizBlock,
    ExerciseBlock,
  },
};

export const stylablePuckConfig = wrapAllComponents({
  ...puckConfig,
  components: Object.fromEntries(
    Object.entries(puckConfig.components).map(([name, definition]) => [
      name,
      withStylablePreview(name, definition as {
        render: (props: Record<string, unknown>) => React.ReactNode;
      }),
    ]),
  ),
});

export function getPuckConfigForPageType(pageType: PageType | null | undefined) {
  if (!pageType || !(PAGE_TYPES as readonly string[]).includes(pageType)) {
    return stylablePuckConfig;
  }

  return {
    ...stylablePuckConfig,
    components: getComponentsForPageType(
      stylablePuckConfig.components as Record<string, unknown>,
      pageType,
    ),
  };
}
